import { useContext, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Button,
  Card,
  Typography,
  CircularProgress,
  alpha,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Skeleton,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import PaymentIcon from '@mui/icons-material/Payment';
import CancelIcon from '@mui/icons-material/Cancel';
import BusinessIcon from '@mui/icons-material/Business';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import InventoryIcon from '@mui/icons-material/Inventory';
import axiosInstance from '../../lib/axios';
import { ColorModeContext } from '../../contexts/ColorModeContext';
import { palette, typography, theme } from '../../theme/colors';
import { useAnimationStyles } from '../../hooks/useAnimations';
import { useSnackbar } from '../../components/SnackbarProvider';
import type { Restock, RestockHistory } from '../../types';
import { RestockStatus } from '../../types';
import { RestockHistoryTimeline } from '../../components/restocks/RestockHistoryTimeline';

const fetchRestock = async (id: string): Promise<Restock> => {
  const response = await axiosInstance.get(`/restocks/${id}`);
  return response.data;
};

const statusLabels: Record<RestockStatus, string> = {
  [RestockStatus.QUOTE]: 'Devis',
  [RestockStatus.ORDERED]: 'Commandé',
  [RestockStatus.DELIVERED]: 'Livré',
  [RestockStatus.PAID]: 'Payé',
  [RestockStatus.CANCELLED]: 'Annulé',
};

const getStatusColor = (status: RestockStatus): string => {
  switch (status) {
    case RestockStatus.QUOTE:
      return palette.warning;
    case RestockStatus.ORDERED:
      return palette.info;
    case RestockStatus.DELIVERED:
      return palette.success;
    case RestockStatus.PAID:
      return palette.primary;
    case RestockStatus.CANCELLED:
      return palette.error;
    default:
      return palette.textMuted;
  }
};

const getNextStatusInfo = (status: RestockStatus): { label: string; description: string } | null => {
  switch (status) {
    case RestockStatus.QUOTE:
      return { label: 'Confirmer la commande', description: 'Passer le statut à "Commandé"' };
    case RestockStatus.ORDERED:
      return { label: 'Marquer comme reçu', description: 'Confirmer la réception et mettre à jour le stock' };
    case RestockStatus.DELIVERED:
      return { label: 'Marquer comme payé', description: 'Confirmer le paiement au fournisseur' };
    default:
      return null;
  }
};

export const RestockShow = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { mode } = useContext(ColorModeContext);
  const { showMessage } = useSnackbar();
  const animations = useAnimationStyles();

  const [confirmDialog, setConfirmDialog] = useState(false);
  const [receiveDialog, setReceiveDialog] = useState(false);
  const [payDialog, setPayDialog] = useState(false);
  const [cancelDialog, setCancelDialog] = useState(false);

  const { data: order, isLoading, error } = useQuery({
    queryKey: ['bulk-restock-order', id],
    queryFn: () => fetchRestock(id!),
    enabled: !!id,
  });

  const { data: history, isLoading: historyLoading } = useQuery({
    queryKey: ['restock-history', id],
    queryFn: async () => {
      const response = await axiosInstance.get(`/restocks/${id}/history`);
      return response.data as RestockHistory[];
    },
    enabled: !!id,
  });

  const confirmMutation = useMutation({
    mutationFn: () => axiosInstance.post(`/restocks/${id}/confirm`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bulk-restock-order', id] });
      queryClient.invalidateQueries({ queryKey: ['restocks'] });
      showMessage('Commande confirmée avec succès', 'success');
      setConfirmDialog(false);
    },
    onError: (err: any) => {
      showMessage(err.response?.data?.message || 'Erreur lors de la confirmation', 'error');
    },
  });

  const receiveMutation = useMutation({
    mutationFn: () => axiosInstance.post(`/restocks/${id}/receive`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bulk-restock-order', id] });
      queryClient.invalidateQueries({ queryKey: ['restocks'] });
      queryClient.invalidateQueries({ queryKey: ['products'] });
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
      showMessage('Commande reçue - Stock mis à jour', 'success');
      setReceiveDialog(false);
    },
    onError: (err: any) => {
      showMessage(err.response?.data?.message || 'Erreur lors de la réception', 'error');
    },
  });

  const markPaidMutation = useMutation({
    mutationFn: () => axiosInstance.post(`/restocks/${id}/mark-paid`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bulk-restock-order', id] });
      queryClient.invalidateQueries({ queryKey: ['restocks'] });
      showMessage('Commande marquée comme payée', 'success');
      setPayDialog(false);
    },
    onError: (err: any) => {
      showMessage(err.response?.data?.message || 'Erreur lors du marquage comme payé', 'error');
    },
  });

  const cancelMutation = useMutation({
    mutationFn: () => axiosInstance.post(`/restocks/${id}/cancel`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bulk-restock-order', id] });
      queryClient.invalidateQueries({ queryKey: ['restocks'] });
      showMessage('Commande annulée', 'success');
      setCancelDialog(false);
    },
    onError: (err: any) => {
      showMessage(err.response?.data?.message || 'Erreur lors de l\'annulation', 'error');
    },
  });

  const duplicateMutation = useMutation({
    mutationFn: () => axiosInstance.post(`/restocks/${id}/duplicate`),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['restocks'] });
      const newOrderId = response.data?.id;
      showMessage('Commande dupliquée avec succès', 'success');
      if (newOrderId) {
        navigate(`/app/restocks/${newOrderId}`);
      } else {
        navigate('/app/restocks');
      }
    },
    onError: (err: any) => {
      showMessage(err.response?.data?.message || 'Erreur lors de la duplication', 'error');
    },
  });

  const colors = mode === 'dark'
    ? {
        bg: palette.background,
        surface: palette.surface,
        surfaceElevated: palette.surfaceElevated,
        border: palette.border,
        textPrimary: palette.textPrimary,
        textSecondary: palette.textSecondary,
        textMuted: palette.textMuted,
      }
    : {
        bg: palette.light.background,
        surface: palette.light.surface,
        surfaceElevated: palette.light.surfaceElevated,
        border: palette.light.border,
        textPrimary: palette.light.textPrimary,
        textSecondary: palette.light.textSecondary,
        textMuted: palette.light.textMuted,
      };

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('fr-DZ', { style: 'currency', currency: 'DZD' }).format(amount);

  const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleDateString('fr-DZ', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });

  const formatDateTime = (dateString: string) =>
    new Date(dateString).toLocaleString('fr-DZ', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });

  if (isLoading) {
    return (
      <Box>
        <Box display="flex" alignItems="center" gap={2} mb={3}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/app/restocks')}
            sx={{ color: colors.textMuted }}
          >
            Retour
          </Button>
          <Skeleton variant="text" width={200} height={40} />
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' }, gap: 3 }}>
          <Skeleton variant="rectangular" height={300} sx={{ borderRadius: theme.radius.sm }} />
          <Skeleton variant="rectangular" height={200} sx={{ borderRadius: theme.radius.sm }} />
        </Box>
      </Box>
    );
  }

  if (error || !order) {
    return (
      <Box>
        <Box display="flex" alignItems="center" gap={2} mb={3}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/app/restocks')}
            sx={{ color: colors.textMuted }}
          >
            Retour
          </Button>
          <Typography variant="h5">Commande introuvable</Typography>
        </Box>
        <Card sx={{ backgroundColor: colors.surface, border: `2px solid ${colors.border}`, p: 3 }}>
          <Typography color="error">
            Impossible de charger les détails de la commande. Elle n'existe peut-être pas ou vous n'avez pas les
            permissions nécessaires.
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate('/app/restocks')}
            sx={{ mt: 2, backgroundColor: palette.primary }}
          >
            Retour aux commandes
          </Button>
        </Card>
      </Box>
    );
  }

  const statusColor = getStatusColor(order.status);
  const nextStatusInfo = getNextStatusInfo(order.status);
  const canCancel = order.status === RestockStatus.QUOTE || order.status === RestockStatus.ORDERED;
  const canEdit = order.status === RestockStatus.QUOTE;

  return (
    <Box sx={{ ...animations.fadeIn }}>
      {/* Header */}
      <Box sx={{ mb: 4, ...animations.slideDown }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/app/restocks')}
          sx={{ color: colors.textMuted, mb: 2, '&:hover': { color: palette.primary } }}
        >
          Retour aux commandes
        </Button>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <LocalShippingIcon sx={{ color: palette.primary, fontSize: 24 }} />
              <Typography
                variant="overline"
                sx={{
                  color: palette.primary,
                  fontFamily: typography.fontFamily.mono,
                  fontSize: typography.fontSize.xs,
                  letterSpacing: typography.letterSpacing.widest,
                  fontWeight: typography.fontWeight.bold,
                }}
              >
                COMMANDE FOURNISSEUR
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography
                variant="h3"
                sx={{
                  fontFamily: typography.fontFamily.display,
                  fontWeight: typography.fontWeight.extrabold,
                  color: colors.textPrimary,
                  letterSpacing: '-0.02em',
                  lineHeight: 1.1,
                }}
              >
                {order.orderNumber}
              </Typography>
              <Chip
                label={statusLabels[order.status].toUpperCase()}
                sx={{
                  backgroundColor: alpha(statusColor, 0.1),
                  color: statusColor,
                  fontFamily: typography.fontFamily.mono,
                  fontSize: typography.fontSize.xs,
                  fontWeight: typography.fontWeight.bold,
                  border: `1px solid ${statusColor}`,
                }}
              />
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {canEdit && (
              <Button
                variant="outlined"
                startIcon={<EditIcon />}
                onClick={() => navigate(`/app/restocks/edit/${id}`)}
                sx={{ borderColor: colors.border, color: colors.textPrimary }}
              >
                Modifier
              </Button>
            )}
            <Button
              variant="outlined"
              startIcon={duplicateMutation.isPending ? <CircularProgress size={16} /> : <ContentCopyIcon />}
              onClick={() => duplicateMutation.mutate()}
              disabled={duplicateMutation.isPending}
              sx={{ borderColor: colors.border, color: colors.textPrimary }}
            >
              {duplicateMutation.isPending ? 'Duplication...' : 'Dupliquer'}
            </Button>
            {order.status === RestockStatus.QUOTE && (
              <Button
                variant="contained"
                startIcon={confirmMutation.isPending ? <CircularProgress size={16} /> : <CheckIcon />}
                onClick={() => setConfirmDialog(true)}
                disabled={confirmMutation.isPending}
                sx={{ backgroundColor: palette.info, color: '#FFFFFF' }}
              >
                {confirmMutation.isPending ? 'Confirmation...' : 'Confirmer'}
              </Button>
            )}
            {order.status === RestockStatus.ORDERED && (
              <Button
                variant="contained"
                startIcon={receiveMutation.isPending ? <CircularProgress size={16} /> : <InventoryIcon />}
                onClick={() => setReceiveDialog(true)}
                disabled={receiveMutation.isPending}
                sx={{ backgroundColor: palette.success, color: '#FFFFFF' }}
              >
                {receiveMutation.isPending ? 'Réception...' : 'Marquer reçu'}
              </Button>
            )}
            {order.status === RestockStatus.DELIVERED && (
              <Button
                variant="contained"
                startIcon={markPaidMutation.isPending ? <CircularProgress size={16} /> : <PaymentIcon />}
                onClick={() => setPayDialog(true)}
                disabled={markPaidMutation.isPending}
                sx={{ backgroundColor: palette.primary, color: '#FFFFFF' }}
              >
                {markPaidMutation.isPending ? 'Traitement...' : 'Marquer payé'}
              </Button>
            )}
            {canCancel && (
              <Button
                variant="outlined"
                startIcon={cancelMutation.isPending ? <CircularProgress size={16} /> : <CancelIcon />}
                onClick={() => setCancelDialog(true)}
                disabled={cancelMutation.isPending}
                sx={{ borderColor: palette.error, color: palette.error }}
              >
                {cancelMutation.isPending ? 'Annulation...' : 'Annuler'}
              </Button>
            )}
          </Box>
        </Box>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '2fr 1fr' }, gap: 3 }}>
        {/* Main Content */}
        <Box>
          {/* Order Details */}
          <Card
            sx={{
              backgroundColor: colors.surface,
              border: `2px solid ${colors.border}`,
              borderRadius: theme.radius.sm,
              p: 3,
              mb: 3,
              ...animations.slideUp(0.1),
            }}
          >
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <BusinessIcon sx={{ fontSize: 18, color: palette.info }} />
                  <Typography variant="overline" sx={{ color: colors.textMuted }}>
                    FOURNISSEUR
                  </Typography>
                </Box>
                <Typography
                  variant="h6"
                  sx={{ color: colors.textPrimary, fontWeight: typography.fontWeight.bold }}
                >
                  {order.supplier?.name || 'N/A'}
                </Typography>
                {order.supplier?.code && (
                  <Typography variant="body2" sx={{ color: colors.textMuted }}>
                    Code: {order.supplier.code}
                  </Typography>
                )}
              </Box>
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <LocationOnIcon sx={{ fontSize: 18, color: palette.warning }} />
                  <Typography variant="overline" sx={{ color: colors.textMuted }}>
                    EMPLACEMENT DE RÉCEPTION
                  </Typography>
                </Box>
                <Typography
                  variant="h6"
                  sx={{ color: colors.textPrimary, fontWeight: typography.fontWeight.bold }}
                >
                  {order.location?.name || 'N/A'}
                </Typography>
              </Box>
            </Box>
          </Card>

          {/* Dates Card */}
          <Card
            sx={{
              backgroundColor: colors.surface,
              border: `2px solid ${colors.border}`,
              borderRadius: theme.radius.sm,
              p: 3,
              mb: 3,
              ...animations.slideUp(0.12),
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <CalendarTodayIcon sx={{ fontSize: 18, color: palette.accent }} />
              <Typography variant="overline" sx={{ color: colors.textMuted }}>
                DATES
              </Typography>
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 2 }}>
              <Box>
                <Typography variant="caption" sx={{ color: colors.textMuted }}>
                  Créée le
                </Typography>
                <Typography variant="body2" sx={{ color: colors.textPrimary, fontWeight: typography.fontWeight.medium }}>
                  {formatDate(order.createdAt)}
                </Typography>
              </Box>
              {order.orderedAt && (
                <Box>
                  <Typography variant="caption" sx={{ color: colors.textMuted }}>
                    Commandée le
                  </Typography>
                  <Typography variant="body2" sx={{ color: colors.textPrimary, fontWeight: typography.fontWeight.medium }}>
                    {formatDateTime(order.orderedAt)}
                  </Typography>
                </Box>
              )}
              {order.deliveredAt && (
                <Box>
                  <Typography variant="caption" sx={{ color: colors.textMuted }}>
                    Reçue le
                  </Typography>
                  <Typography variant="body2" sx={{ color: colors.textPrimary, fontWeight: typography.fontWeight.medium }}>
                    {formatDateTime(order.deliveredAt)}
                  </Typography>
                </Box>
              )}
              {order.paidAt && (
                <Box>
                  <Typography variant="caption" sx={{ color: colors.textMuted }}>
                    Payée le
                  </Typography>
                  <Typography variant="body2" sx={{ color: colors.textPrimary, fontWeight: typography.fontWeight.medium }}>
                    {formatDateTime(order.paidAt)}
                  </Typography>
                </Box>
              )}
            </Box>
          </Card>

          {/* Line Items */}
          <Card
            sx={{
              backgroundColor: colors.surface,
              border: `2px solid ${colors.border}`,
              borderRadius: theme.radius.sm,
              p: 3,
              mb: 3,
              ...animations.slideUp(0.15),
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontFamily: typography.fontFamily.display,
                fontWeight: typography.fontWeight.bold,
                color: colors.textPrimary,
                mb: 3,
                pb: 1,
                borderBottom: `2px solid ${colors.border}`,
              }}
            >
              PRODUITS COMMANDÉS
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ color: colors.textMuted, fontFamily: typography.fontFamily.mono, fontSize: typography.fontSize.xs }}>
                      Produit
                    </TableCell>
                    <TableCell align="center" sx={{ color: colors.textMuted, fontFamily: typography.fontFamily.mono, fontSize: typography.fontSize.xs }}>
                      Quantité
                    </TableCell>
                    <TableCell align="right" sx={{ color: colors.textMuted, fontFamily: typography.fontFamily.mono, fontSize: typography.fontSize.xs }}>
                      Coût unitaire
                    </TableCell>
                    <TableCell align="right" sx={{ color: colors.textMuted, fontFamily: typography.fontFamily.mono, fontSize: typography.fontSize.xs }}>
                      Sous-total
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {order.items?.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell>
                        <Typography sx={{ color: colors.textPrimary, fontWeight: typography.fontWeight.medium }}>
                          {item.productName}
                        </Typography>
                        {item.product?.sku && (
                          <Typography variant="caption" sx={{ color: colors.textMuted }}>
                            SKU: {item.product.sku}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={item.quantity}
                          size="small"
                          sx={{
                            backgroundColor: alpha(palette.primary, 0.1),
                            color: palette.primary,
                            fontFamily: typography.fontFamily.mono,
                            fontWeight: typography.fontWeight.bold,
                          }}
                        />
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ fontFamily: typography.fontFamily.mono, color: colors.textPrimary }}
                      >
                        {formatCurrency(item.unitCost)}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          fontFamily: typography.fontFamily.mono,
                          fontWeight: typography.fontWeight.bold,
                          color: palette.success,
                        }}
                      >
                        {formatCurrency(item.subtotal)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>

          {/* Notes */}
          {order.notes && (
            <Card
              sx={{
                backgroundColor: colors.surface,
                border: `2px solid ${colors.border}`,
                borderRadius: theme.radius.sm,
                p: 3,
                ...animations.slideUp(0.2),
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontFamily: typography.fontFamily.display,
                  fontWeight: typography.fontWeight.bold,
                  color: colors.textPrimary,
                  mb: 2,
                  pb: 1,
                  borderBottom: `2px solid ${colors.border}`,
                }}
              >
                NOTES
              </Typography>
              <Typography variant="body2" sx={{ color: colors.textPrimary, whiteSpace: 'pre-wrap' }}>
                {order.notes}
              </Typography>
            </Card>
          )}
        </Box>

        {/* Sidebar */}
        <Box>
          {/* Totals */}
          <Card
            sx={{
              backgroundColor: colors.surface,
              border: `2px solid ${colors.border}`,
              borderRadius: theme.radius.sm,
              p: 3,
              mb: 3,
              position: 'sticky',
              top: 20,
              ...animations.slideUp(0.1),
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontFamily: typography.fontFamily.display,
                fontWeight: typography.fontWeight.bold,
                color: colors.textPrimary,
                mb: 3,
                pb: 1,
                borderBottom: `2px solid ${colors.border}`,
              }}
            >
              RÉSUMÉ
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography sx={{ color: colors.textMuted }}>Nombre d'articles:</Typography>
                <Typography sx={{ fontFamily: typography.fontFamily.mono, color: colors.textPrimary }}>
                  {order.items?.length || 0}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography sx={{ color: colors.textMuted }}>Quantité totale:</Typography>
                <Typography sx={{ fontFamily: typography.fontFamily.mono, color: colors.textPrimary }}>
                  {order.items?.reduce((sum, item) => sum + item.quantity, 0) || 0}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography sx={{ color: colors.textMuted }}>Sous-total:</Typography>
                <Typography sx={{ fontFamily: typography.fontFamily.mono, color: colors.textPrimary }}>
                  {formatCurrency(order.subtotal)}
                </Typography>
              </Box>
              {order.tax > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography sx={{ color: colors.textMuted }}>TVA:</Typography>
                  <Typography sx={{ fontFamily: typography.fontFamily.mono, color: colors.textPrimary }}>
                    {formatCurrency(order.tax)}
                  </Typography>
                </Box>
              )}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  pt: 2,
                  borderTop: `2px solid ${colors.border}`,
                }}
              >
                <Typography
                  sx={{
                    fontWeight: typography.fontWeight.bold,
                    color: colors.textPrimary,
                    fontSize: typography.fontSize.lg,
                  }}
                >
                  TOTAL:
                </Typography>
                <Typography
                  sx={{
                    fontFamily: typography.fontFamily.mono,
                    fontWeight: typography.fontWeight.extrabold,
                    color: palette.success,
                    fontSize: typography.fontSize.xl,
                  }}
                >
                  {formatCurrency(order.totalAmount)}
                </Typography>
              </Box>
            </Box>

            {/* Status Info */}
            {nextStatusInfo && (
              <Box
                sx={{
                  mt: 3,
                  p: 2,
                  backgroundColor: alpha(palette.info, 0.1),
                  borderRadius: theme.radius.sm,
                  border: `1px solid ${alpha(palette.info, 0.2)}`,
                }}
              >
                <Typography variant="body2" sx={{ color: palette.info, fontWeight: typography.fontWeight.bold, mb: 0.5 }}>
                  Prochaine étape
                </Typography>
                <Typography variant="caption" sx={{ color: colors.textMuted }}>
                  {nextStatusInfo.description}
                </Typography>
              </Box>
            )}

            {/* Status Messages */}
            {order.status === RestockStatus.DELIVERED && (
              <Box
                sx={{
                  mt: 3,
                  p: 2,
                  backgroundColor: alpha(palette.success, 0.1),
                  borderRadius: theme.radius.sm,
                  border: `1px solid ${alpha(palette.success, 0.2)}`,
                }}
              >
                <Typography variant="body2" sx={{ color: palette.success, fontWeight: typography.fontWeight.bold }}>
                  Stock mis à jour
                </Typography>
                <Typography variant="caption" sx={{ color: colors.textMuted }}>
                  L'inventaire a été mis à jour lors de la réception.
                </Typography>
              </Box>
            )}

            {order.status === RestockStatus.PAID && (
              <Box
                sx={{
                  mt: 3,
                  p: 2,
                  backgroundColor: alpha(palette.primary, 0.1),
                  borderRadius: theme.radius.sm,
                  border: `1px solid ${alpha(palette.primary, 0.2)}`,
                }}
              >
                <Typography variant="body2" sx={{ color: palette.primary, fontWeight: typography.fontWeight.bold }}>
                  Commande finalisée
                </Typography>
                <Typography variant="caption" sx={{ color: colors.textMuted }}>
                  Cette commande a été payée et est complète.
                </Typography>
              </Box>
            )}

            {order.status === RestockStatus.CANCELLED && (
              <Box
                sx={{
                  mt: 3,
                  p: 2,
                  backgroundColor: alpha(palette.error, 0.1),
                  borderRadius: theme.radius.sm,
                  border: `1px solid ${alpha(palette.error, 0.2)}`,
                }}
              >
                <Typography variant="body2" sx={{ color: palette.error, fontWeight: typography.fontWeight.bold }}>
                  Commande annulée
                </Typography>
                <Typography variant="caption" sx={{ color: colors.textMuted }}>
                  Cette commande a été annulée et n'aura aucun impact sur l'inventaire.
                </Typography>
              </Box>
            )}
          </Card>

          {/* History Timeline */}
          <Card
            sx={{
              backgroundColor: colors.surface,
              border: `2px solid ${colors.border}`,
              borderRadius: theme.radius.sm,
              p: 3,
              ...animations.slideUp(0.15),
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontFamily: typography.fontFamily.display,
                fontWeight: typography.fontWeight.bold,
                color: colors.textPrimary,
                mb: 3,
                pb: 1,
                borderBottom: `2px solid ${colors.border}`,
              }}
            >
              HISTORIQUE
            </Typography>
            <RestockHistoryTimeline history={history} isLoading={historyLoading} />
          </Card>
        </Box>
      </Box>

      {/* Confirm Dialog */}
      <Dialog open={confirmDialog} onClose={() => setConfirmDialog(false)}>
        <DialogTitle>Confirmer la commande</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Êtes-vous sûr de vouloir confirmer cette commande ? Le statut passera de "Devis" à "Commandé".
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog(false)}>Annuler</Button>
          <Button
            variant="contained"
            onClick={() => confirmMutation.mutate()}
            disabled={confirmMutation.isPending}
            sx={{ backgroundColor: palette.info }}
          >
            {confirmMutation.isPending ? 'Confirmation...' : 'Confirmer'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Receive Dialog */}
      <Dialog open={receiveDialog} onClose={() => setReceiveDialog(false)}>
        <DialogTitle>Confirmer la réception</DialogTitle>
        <DialogContent>
          <DialogContentText>
            <strong>Attention :</strong> Cette action mettra à jour le stock de tous les produits de cette commande
            dans l'emplacement "{order.location?.name}".
            <br /><br />
            Êtes-vous sûr de vouloir marquer cette commande comme reçue ?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReceiveDialog(false)}>Annuler</Button>
          <Button
            variant="contained"
            onClick={() => receiveMutation.mutate()}
            disabled={receiveMutation.isPending}
            sx={{ backgroundColor: palette.success }}
          >
            {receiveMutation.isPending ? 'Traitement...' : 'Confirmer la réception'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Mark Paid Dialog */}
      <Dialog open={payDialog} onClose={() => setPayDialog(false)}>
        <DialogTitle>Marquer comme payé</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Confirmez-vous que le paiement de {formatCurrency(order.totalAmount)} a été effectué au fournisseur "{order.supplier?.name}" ?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPayDialog(false)}>Annuler</Button>
          <Button
            variant="contained"
            onClick={() => markPaidMutation.mutate()}
            disabled={markPaidMutation.isPending}
            sx={{ backgroundColor: palette.primary }}
          >
            {markPaidMutation.isPending ? 'Traitement...' : 'Confirmer le paiement'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cancel Dialog */}
      <Dialog open={cancelDialog} onClose={() => setCancelDialog(false)}>
        <DialogTitle>Annuler la commande</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Êtes-vous sûr de vouloir annuler cette commande ? Cette action est irréversible.
            {order.status === RestockStatus.QUOTE && (
              <><br /><br />Le devis sera annulé et la commande ne pourra plus être traitée.</>
            )}
            {order.status === RestockStatus.ORDERED && (
              <><br /><br />La commande sera annulée. Aucun impact sur l'inventaire car la livraison n'a pas encore été reçue.</>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDialog(false)}>Retour</Button>
          <Button
            variant="contained"
            onClick={() => cancelMutation.mutate()}
            disabled={cancelMutation.isPending}
            sx={{ backgroundColor: palette.error }}
          >
            {cancelMutation.isPending ? 'Annulation...' : 'Annuler la commande'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RestockShow;
